/**
 * Pusher Real-time Communication Utility
 * Provides Pusher client instance and constants for real-time events
 */

import type { Authorizer, Channel } from 'pusher-js';
import Pusher from 'pusher-js';
import { getCommanderClientHeaders } from './activityTracker';

/**
 * Initialize Pusher client with environment configuration
 * Note: Pusher instance is created lazily to avoid initialization in SSR
 */
let pusherInstance: Pusher | null = null;

export function isPusherConfigured(): boolean {
  const pusherKey = import.meta.env.VITE_PUSHER_APP_KEY;
  const pusherCluster = import.meta.env.VITE_PUSHER_CLUSTER;

  return Boolean(
    pusherKey &&
      pusherCluster &&
      pusherKey !== 'your-app-key' &&
      pusherCluster !== 'your-cluster-name'
  );
}

function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const cookieValue = document.cookie
      .split('; ')
      .find((row) => row.startsWith('auth_token='))
      ?.substring('auth_token='.length);

    if (cookieValue) {
      return decodeURIComponent(cookieValue);
    }

    return localStorage.getItem('auth_token');
  } catch (error) {
    console.warn('[Pusher] Failed to retrieve auth token:', error);
    return null;
  }
}

function getApiBaseUrl(): string {
  return (
    import.meta.env.VITE_BASE_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:3011'
  );
}

function buildAuthEndpoint(path: string): string {
  const baseUrl = getApiBaseUrl();
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
}

/**
 * Fetch-based channel authorizer for private Pusher channels.
 */
function createFetchChannelAuthorizer(channel: Channel, authEndpoint: string): Authorizer {
  return {
    authorize: (
      socketId: string,
      callback: (err: Error | null, auth: { auth: string; channel_data?: string }) => void
    ) => {
      const token = getAuthToken();

      fetch(authEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getCommanderClientHeaders(),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          socket_id: socketId,
          channel_name: channel.name,
        }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Pusher auth failed: ${response.status} ${response.statusText}`);
          }
          return response.json();
        })
        .then((data) => {
          callback(null, data);
        })
        .catch((error) => {
          callback(
            error instanceof Error ? error : new Error(error.message || 'Pusher auth failed'),
            { auth: '' }
          );
        });
    },
  };
}

function resolveChannelAuthorizer(channel: Channel): Authorizer {
  if (channel.name.startsWith('research-')) {
    return createFetchChannelAuthorizer(channel, buildAuthEndpoint('/api/research/pusher-auth'));
  }

  if (
    channel.name.startsWith('private-heatmap-user-') ||
    channel.name.startsWith('private-heatmap-chat-')
  ) {
    return createFetchChannelAuthorizer(channel, buildAuthEndpoint('/heatmap-chat/pusher-auth'));
  }

  if (channel.name.startsWith('private-user-') || channel.name.startsWith('private-tenant-')) {
    return createFetchChannelAuthorizer(channel, buildAuthEndpoint('/pusher-auth'));
  }

  if (channel.name.startsWith('private-')) {
    return {
      authorize: (
        _socketId: string,
        callback: (err: Error | null, auth: { auth: string; channel_data?: string }) => void
      ) => {
        callback(new Error(`No Pusher authorizer configured for channel: ${channel.name}`), {
          auth: '',
        });
      },
    };
  }

  // Public channels do not require signed auth.
  return {
    authorize: (
      _socketId: string,
      callback: (err: Error | null, auth: { auth: string; channel_data?: string }) => void
    ) => {
      callback(null, { auth: '' });
    },
  };
}

export const getPusherClient = (): Pusher => {
  if (!pusherInstance) {
    const pusherKey = import.meta.env.VITE_PUSHER_APP_KEY;
    const pusherCluster = import.meta.env.VITE_PUSHER_CLUSTER;

    if (!pusherKey || !pusherCluster) {
      throw new Error(
        'Pusher configuration missing. Please set VITE_PUSHER_APP_KEY and VITE_PUSHER_CLUSTER environment variables.'
      );
    }

    pusherInstance = new Pusher(pusherKey, {
      cluster: pusherCluster,
      authorizer: (channel: Channel) => resolveChannelAuthorizer(channel),
    });
  }

  return pusherInstance;
};

/**
 * Pusher channel names
 */
export const PUSHER_CHANNELS = {
  USER: (userId: string) => `private-user-${userId}`,
  // Tenant + territory scoped channel — only CompanyEmployees whose territory
  // scope covers `territoryId` may subscribe (see backend pusherAuth.routes.js).
  // `territoryId` is nullable (unassigned deals) and normalized to a fixed
  // 'unassigned' segment so it's a valid channel name.
  TENANT: (tenantId: string, territoryId?: string) =>
    `private-tenant-${tenantId}-territory-${territoryId || 'unassigned'}`,
  // Heatmap channels
  HEATMAP_USER: (userId: string) => `private-heatmap-user-${userId}`,
  HEATMAP_CHAT: (threadId: string) => `private-heatmap-chat-${threadId}`,
  // HaulerCentral channels
  HAULERCENTRAL_RFQ: (rfqId: string) => `haulercentral-rfq-${rfqId}`,
  HAULERCENTRAL_USER_RFQS: (userId: string) => `haulercentral-user-rfqs-${userId}`,
  HAULERCENTRAL_HAULER_RFQS: (haulerProfileId: string) =>
    `haulercentral-hauler-rfqs-${haulerProfileId}`,
  // Routing channels
  ROUTING_ROUTE: (routeId: string) => `routing-route-${routeId}`,
};

/**
 * Pusher event names
 */
export const PUSHER_EVENTS = {
  AI_GENERATION_COMPLETED: 'ai-generation-completed',
  AI_GENERATION_FAILED: 'ai-generation-failed',
  NOTIFICATION_EMAIL_RECEIVED: 'communication.email.received',
  NOTIFICATION_SMS_RECEIVED: 'communication.sms.received',
  NOTIFICATION_TASK_ASSIGNED: 'task.assigned',
  // A sequence's Notify step, opt-out notices and owner notices
  NOTIFICATION_SEQUENCE_NOTIFY: 'sequence.notify',
  // A Signals opportunity alert sent to the rep
  NOTIFICATION_SIGNAL_ALERT: 'signal.alert',
  // Heatmap events - job notifications (sent on private-heatmap-user-${userId} channel)
  HEATMAP_SEARCH_COMPLETE: 'search-complete',
  HEATMAP_TARGETING_COMPLETE: 'targeting-complete',
  HEATMAP_JOB_FAILED: 'job-failed',
  HEATMAP_SMART_REASONING_REFRESHED: 'smart-reasoning-refreshed',
  // Heatmap events - chat/streaming (sent on heatmap-chat-${threadId} channel)
  HEATMAP_CHAT_RESPONSE: 'chat-response',
  HEATMAP_MAP_STATE_CHANGED: 'map-state-changed',
  // HaulerCentral RFQ events
  HAULERCENTRAL_RFQ_CREATED: 'haulercentral-rfq-created',
  HAULERCENTRAL_RFQ_OPENED: 'haulercentral-rfq-opened',
  HAULERCENTRAL_RFQ_STATUS_CHANGED: 'haulercentral-rfq-status-changed',
  HAULERCENTRAL_QUOTE_RECEIVED: 'haulercentral-quote-received',
  HAULERCENTRAL_QUOTE_WITHDRAWN: 'haulercentral-quote-withdrawn',
  HAULERCENTRAL_RFQ_CLOSED: 'haulercentral-rfq-closed',
  HAULERCENTRAL_RFQ_AWARDED: 'haulercentral-rfq-awarded',
  HAULERCENTRAL_RFQ_CANCELLED: 'haulercentral-rfq-cancelled',
  // Demo ICP prospecting
  DEMO_PROSPECTS_REFRESHED: 'demo-prospects-refreshed',
  ACTION_DASH_CARDS_CHANGED: 'action-dash-cards-changed',
  // Routing driver alerts
  ROUTING_DRIVER_ALERT: 'routing-driver-alert',
  // Communication analysis
  CALL_ANALYSIS_COMPLETED: 'call-analysis-completed',
  CALL_CHAT_RESPONSE_READY: 'call-chat-response-ready',
  // Email analysis
  EMAIL_ANALYSIS_COMPLETED: 'email-analysis-completed',
  EMAIL_CHAT_RESPONSE_READY: 'email-chat-response-ready',
  // Prospect contact events
  PROSPECT_CONTACT_FOUND: 'prospect.contact_found',
  // Batch push of a prospect's contacts to the user's connected CRMs
  // Prospecting email draft generation settled — success or failure alike
  PROSPECTING_DRAFT_SETTLED: 'prospecting-draft-settled',
  PROSPECT_CRM_PUSH_COMPLETED: 'prospect-crm-push-completed',
  PROSPECT_CRM_PUSH_FAILED: 'prospect-crm-push-failed',
  // CRM deals — live-refresh open Account/Deal pages
  DEAL_CREATED: 'deal.created',
  DEAL_UPDATED: 'deal.updated',
  // Quote view log — fired whenever a new QuoteViewLog row is written from
  // any channel (email open / docusign delivery / documenso delivery /
  // public-page view), so an open Quote Details page refreshes its view
  // count and chronological log without a manual reload.
  QUOTE_VIEW_LOGGED: 'quote.view-logged',
  // Gmail signature import (onboarding) — settled, success or failure
  GMAIL_SIGNATURE_IMPORT_SETTLED: 'gmail-signature-import-settled',
};
